/**
 * <sticky-header-bar>
 *
 * Measures the bar and publishes the two sizes its CSS needs:
 *
 *   --sh-ann-height  (on the section wrapper, "top" variant)
 *     Height of the announcement row. The wrapper is `position: sticky` with
 *     `top: calc(-1 * var(--sh-ann-height))`, so the block scrolls normally
 *     until the announcement row has cleared the top of the viewport and the
 *     CTA row lands at 0.
 *
 *   --sh-height  (on :root, "bottom" variant)
 *     Total height of the bar. That variant is position:fixed and out of flow,
 *     so body padding reserves its space.
 *
 * Markup contract:
 *   <sticky-header-bar data-position="top|bottom">
 *     <div data-announcement>…</div>   (optional)
 *     <div>…headline + CTA…</div>
 *   </sticky-header-bar>
 *
 * Deliberately has no scroll listener, and never reads --header-offset:
 * header.js rewrites that on every scroll tick.
 */
if (!customElements.get('sticky-header-bar')) {
  class StickyHeaderBar extends HTMLElement {
    connectedCallback() {
      if (this._ready) {
        this._observe();
        return;
      }
      this._ready = true;

      this._atBottom = this.dataset.position === 'bottom';
      this._wrapper = this.closest('.shopify-section') || this.parentElement;
      this._announcement = this.querySelector('[data-announcement]');
      this._bar = this.querySelector('[data-bar]');

      this._publish();
      this._observe();
    }

    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      if (this._resizeTimer) clearTimeout(this._resizeTimer);

      // Don't leave dead padding or a stale offset behind when the section is
      // removed or re-rendered in the theme editor.
      document.documentElement.style.removeProperty('--sh-height');
      document.documentElement.style.removeProperty('--anchor-offset');
      this._wrapper?.style.removeProperty('--sh-ann-height');

      this._ready = false;
    }

    _publish() {
      if (this._atBottom) {
        const height = this.offsetHeight;
        if (height) {
          document.documentElement.style.setProperty('--sh-height', `${height}px`);
        }
        return;
      }

      if (!this._wrapper) return;

      // No announcement row means 0px, which degrades to a plain top:0 sticky.
      const announcementHeight = this._announcement ? this._announcement.offsetHeight : 0;
      this._wrapper.style.setProperty('--sh-ann-height', `${announcementHeight}px`);

      // Once pinned, only the bar row covers the page — that is how far an
      // anchor has to clear. Consumed by .section-anchor's scroll-margin-top.
      const barHeight = this._bar ? this._bar.offsetHeight : 0;
      if (barHeight) {
        document.documentElement.style.setProperty('--anchor-offset', `${barHeight}px`);
      }
    }

    _observe() {
      if (this._observer || !('ResizeObserver' in window)) return;

      this._observer = new ResizeObserver(() => {
        if (this._resizeTimer) clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => this._publish(), 150);
      });
      this._observer.observe(this);
    }
  }

  customElements.define('sticky-header-bar', StickyHeaderBar);
}
